// Auto-renders a chart for chat-api tool results whose shape obviously
// suits it. Today: scans_by_period → line chart of entries/exits/warnings
// over time, and count_attendees_by → pie chart of buckets. Other tools
// fall back to the table — no change.
//
// The rule is intentionally narrow (by tool name + a guard on shape). When
// a new tool deserves a chart, add a branch to `pickChart`.

import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";

const PIE_COLORS = [
  "#2563eb",
  "#16a34a",
  "#f59e0b",
  "#dc2626",
  "#7c3aed",
  "#0ea5e9",
  "#db2777",
  "#475569",
  "#65a30d",
  "#c2410c"
];

interface LineChartPick {
  type: "line";
  rows: Array<Record<string, unknown>>;
  xKey: string;
  yKeys: string[];
}

interface PieChartPick {
  type: "pie";
  rows: Array<{ name: string; value: number }>;
}

type ChartPick = LineChartPick | PieChartPick;

/** Decides whether to render a chart, and if so which kind. */
export function pickChart(toolName: string, result: unknown): ChartPick | null {
  if (!result || typeof result !== "object") {
    return null;
  }
  const obj = result as Record<string, unknown>;

  if (toolName === "scans_by_period") {
    const periods = obj["periods"];
    if (
      Array.isArray(periods) &&
      periods.length >= 2 &&
      periods.every((p) => p && typeof p === "object" && "bucket_start" in (p as object))
    ) {
      return {
        type: "line",
        rows: periods.map((p) => ({
          ...(p as Record<string, unknown>),
          bucket_start: formatTimestamp(
            String((p as { bucket_start?: unknown }).bucket_start ?? "")
          )
        })),
        xKey: "bucket_start",
        yKeys: ["entries", "exits", "warnings"]
      };
    }
  }

  if (toolName === "count_attendees_by") {
    const buckets = obj["buckets"];
    if (Array.isArray(buckets) && buckets.length >= 2) {
      const pieRows = buckets
        .filter(
          (b): b is { bucket: unknown; count: unknown } =>
            !!b && typeof b === "object" && "bucket" in b && "count" in b
        )
        .map((b) => ({
          name: String(b.bucket ?? "(none)"),
          value: Number(b.count) || 0
        }))
        // Drop empty slices — recharts renders them as visual noise.
        .filter((r) => r.value > 0);
      if (pieRows.length >= 2) {
        return { type: "pie", rows: pieRows };
      }
    }
  }

  return null;
}

function formatTimestamp(iso: string): string {
  if (!iso) return iso;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // YYYY-MM-DD HH:mm — compact enough for the X-axis.
  return d.toISOString().slice(0, 16).replace("T", " ");
}

export function ResultChart({ chart }: { chart: ChartPick }): JSX.Element {
  if (chart.type === "line") {
    return (
      <div className="h-72 w-full rounded border border-border bg-background p-2">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chart.rows} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey={chart.xKey}
              tick={{ fontSize: 10 }}
              minTickGap={20}
              tickFormatter={(v: string) => v.slice(5)} // drop year
            />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line
              type="monotone"
              dataKey="entries"
              stroke="#16a34a"
              strokeWidth={2}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="exits"
              stroke="#dc2626"
              strokeWidth={2}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="warnings"
              stroke="#f59e0b"
              strokeWidth={1.5}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // Pie
  return (
    <div className="h-72 w-full rounded border border-border bg-background p-2">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={chart.rows}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius={90}
            innerRadius={36}
            label={(d: { name?: string; value?: number }) =>
              d.name && d.value !== undefined ? `${d.name} (${d.value})` : ""
            }
          >
            {chart.rows.map((_, idx) => (
              <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: 11 }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}
